import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import formatDateTime from "components/Shared/formatDateTime"
import TableShimmer from "components/Shared/TableShimmer"
import { useBrandOptions } from "hooks"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Badge, Button, Card, CardBody, Col, Row, Table } from "reactstrap"
// import { getBrandList } from "store/actions/brandsAction"
import { getCountryList } from "store/actions/countriesAction"
import { getMenusWithoutPagination } from "store/actions/menuSetupAction"
import { getPosBranches, syncPosBranch } from "store/actions/posAction"
import { showAlert } from "store/reducers/alertReducer"
import { getMenuSelectLabel } from "utils/menuSelectUtils"
import {
  getBranchSyncListColumns,
  getPosBranchLinkColumnLabel,
  isPosBranchLinked,
  resolvePosIntegrationFromContext,
} from "utils/posBranchSyncUtils"

const BranchSync = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Branch Sync ")

  const dispatch = useDispatch()
  const [selectedId, setSelectedId] = useState(null)

  const {
    loading,
    isLoading,
    itemsPerPage,
    currentPage,
    totalPages,
    totalCount,
    posBranches,
  } = useSelector(state => state.POS)
  // const { brandList } = useSelector(state => state.Brands)
  const { allMenus } = useSelector(state => state.MenuSetup)
  const { countryList } = useSelector(state => state.Countries)
  const { allowedBrands, hasOnlyOneBrand } = useBrandOptions()

  const navigate = useNavigate()
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    status: 2,
    sortOrder: 1,
    brand: "",
    menu: "",
    country: "",
  })

  const formattedBrands = useMemo(() => {
    if (!allowedBrands) return []

    return allowedBrands?.map(brand => {
      return {
        label: brand.brandName,
        value: brand._id,
      }
    })
  }, [allowedBrands])

  const formattedMenus = useMemo(() => {
    if (!allMenus) return []

    return allMenus.map(menu => {
      return {
        label: getMenuSelectLabel(menu, language),
        value: menu._id,
      }
    })
  }, [allMenus, language])

  const formattedCountries = useMemo(() => {
    if (!countryList) return []

    return countryList.map(country => {
      return {
        label: language === "en" ? country.name : country.nameAr,
        value: country._id,
      }
    })
  }, [countryList, language])

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
      colSize: 4,
    },

    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
      colSize: 4,
    },
    {
      name: "brand",
      type: "select",
      options: formattedBrands,
      placeholder: t("Select Brand"),
      colSize: 4,
    },
    {
      name: "menu",
      type: "select",
      options: formattedMenus,
      placeholder: t("Select Menu"),
      colSize: 4,
    },
    {
      name: "country",
      type: "select",
      options: formattedCountries,
      placeholder: t("Select Country"),
      colSize: 4,
    },
  ]

  const { userRole, accountType } = useSelector(state => state.Login)

  const [permissions, setPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      29,
      "branch_sync_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      29,
      "branch_sync_3",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  useEffect(() => {
    fetchPOSBranch()
    // dispatch(getBrandList())
    dispatch(getMenusWithoutPagination())
    dispatch(getCountryList())
  }, [allowedBrands])

  const showPosBranchesError = (payload, fallback) => {
    const text =
      payload?.msg ||
      payload?.message ||
      payload?.error ||
      fallback ||
      t("Something went wrong")
    dispatch(showAlert({ text, type: "error" }))
  }

  const fetchPOSBranch = () => {
    let selectedBrand = null
    if (allowedBrands && allowedBrands?.length == 0) return

    if (hasOnlyOneBrand) selectedBrand = allowedBrands && allowedBrands[0]?._id

    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
      ...(selectedBrand ? { brand: selectedBrand } : {}),
    }
    dispatch(getPosBranches(payload))
      .then(action => {
        const { statusCode, status } = action?.payload || {}
        if (!(statusCode === 200 && status)) {
          showPosBranchesError(action?.payload)
        }
      })
      .catch(() => {
        showPosBranchesError(null)
      })
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleFetchPosBranches = (page = 1) => {
    const { status, sortOrder, country, menu, brand } = selectedFilters
    let selectedBrand = brand

    if (allowedBrands && allowedBrands?.length == 0) return

    if (hasOnlyOneBrand) selectedBrand = allowedBrands && allowedBrands[0]?._id

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      ...(country ? { country } : {}),
      ...(menu ? { menu } : {}),
      ...(selectedBrand ? { brand: selectedBrand } : {}),
    }

    dispatch(getPosBranches(payload))
      .then(action => {
        const { statusCode, status, msg, message } = action?.payload || {}
        if (statusCode === 200 && status) {
          navigate(`/branch-sync?page=${page}`)
          return
        }
        showPosBranchesError(action?.payload, msg || message)
      })
      .catch(() => {
        showPosBranchesError(null)
      })
  }

  const handleSearch = () => {
    handleFetchPosBranches(1)
  }

  const onPageChange = page => {
    handleFetchPosBranches(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
      brand: "",
      menu: "",
      country: "",
    })
    setSearchTerm("")
    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getPosBranches(payload))
      .then(action => {
        const { statusCode, status } = action?.payload || {}
        if (statusCode === 200 && status) {
          navigate(`/branch-sync?page=1`)
          return
        }
        showPosBranchesError(action?.payload)
      })
      .catch(() => {
        showPosBranchesError(null)
      })
  }

  const posIntegration = useMemo(
    () =>
      resolvePosIntegrationFromContext({
        brandId: selectedFilters.brand,
        allowedBrands,
      }),
    [selectedFilters.brand, allowedBrands]
  )

  const listColumns = useMemo(
    () => getBranchSyncListColumns(posIntegration),
    [posIntegration]
  )

  const columnHeaders = useMemo(
    () => ({
      sno: t("S. No."),
      name: t("Branch Name"),
      branchRef: t("Branch Reference"),
      countryCode: t("Country Code"),
      posBranchLink: getPosBranchLinkColumnLabel(posIntegration, t),
      organizationId: t("Organization Id"),
      terminalGroup: t("Terminal Group Id"),
      webhook: t("Webhook URL"),
      paymentType: t("Payment Type Id"),
      lastUpdated: t("Last Updated"),
      action: t("Action"),
    }),
    [posIntegration, t]
  )

  const renderConnectedBadge = connected =>
    connected ? (
      <Badge className="bg-success">{t("Connected")}</Badge>
    ) : (
      <Badge className="bg-danger">{t("Not Connected")}</Badge>
    )

  const renderSetupBadge = configured =>
    configured ? (
      <Badge className="bg-success">{t("Setup")}</Badge>
    ) : (
      <Badge className="bg-danger">{t("Not Setup")}</Badge>
    )

  const renderListCell = (columnKey, item, index) => {
    const ps = item?.posSettings || {}
    switch (columnKey) {
      case "sno":
        return (currentPage - 1) * itemsPerPage + index + 1
      case "name":
        return language === "sa" ? item.nameAr : item.name
      case "branchRef":
        return item.branchId || "-"
      case "countryCode":
        return item.country
      case "posBranchLink":
        return renderConnectedBadge(isPosBranchLinked(item, posIntegration))
      case "organizationId":
        return renderConnectedBadge(!!ps.organizationId)
      case "terminalGroup":
        return renderConnectedBadge(!!ps.terminalGroupId)
      case "webhook":
        return renderSetupBadge(!!ps.webhookUrl)
      case "paymentType":
        return renderConnectedBadge(!!ps.paymentTypeId)
      case "lastUpdated":
        return formatDateTime(ps.updatedAt)
      case "action":
        return (
          <Button
            color="primary"
            disabled={selectedId === item._id && isLoading}
            onClick={() => {
              handleBranchSync(item._id, item.country)
              setSelectedId(item._id)
            }}
          >
            {t("Sync")}
          </Button>
        )
      default:
        return "-"
    }
  }

  const handleBranchSync = (id, countryCode) => {
    const payload = {
      countryCode,
      id,
    }
    dispatch(syncPosBranch(payload))
      .then(action => {
        const { statusCode, status, success, msg, message } = action?.payload
        if ((statusCode === 200 && status) || success) {
          dispatch(showAlert({ text: msg || message, type: "success" }))
          setSelectedId(null)
          handleFetchPosBranches(currentPage)
        } else {
          dispatch(
            showAlert({
              text: msg || message || t("Something went wrong"),
              type: "error",
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({ text: t("Something went wrong"), type: "error" })
        )
      })
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown cardTitle={t("POS Branches")} />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                />

                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : posBranches.length === 0 ? (
                    <p className="text-center my-2">{t("No data found")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            {listColumns
                              .filter(
                                key =>
                                  key !== "action" || permissions.edit
                              )
                              .map(key => (
                                <th key={key}>{columnHeaders[key]}</th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {posBranches.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(`/branch-sync/details?id=${item._id}`)
                              }}
                              className="cursor-pointer"
                            >
                              {listColumns
                                .filter(
                                  key =>
                                    key !== "action" || permissions.edit
                                )
                                .map(key => {
                                  const isAction = key === "action"
                                  const CellTag = key === "sno" ? "th" : "td"
                                  return (
                                    <CellTag
                                      key={key}
                                      scope={key === "sno" ? "row" : undefined}
                                      onClick={
                                        isAction
                                          ? e => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                            }
                                          : undefined
                                      }
                                    >
                                      {renderListCell(key, item, index)}
                                    </CellTag>
                                  )
                                })}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
                {!loading && posBranches.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={posBranches}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BranchSync
